import { Problem } from "./Problem";

export interface ApiResponse<T> {
  data?: T;
  headers: Headers;
  status: number;
  statusText: string;
  url: string;
  ok: boolean;
  problem?: Problem;
  text: string;
}

export enum ApplicationPlatform {
  Newsapp = "app",
}

export interface Slide {
  id: string;
  title: string;
  topline?: string;
  description?: string;
  background?: string;
  backgroundSource?: string;
  backgroundCopyright?: string;
  questionImage?: string;
  questionImageCopyright?: string;
  questionImageSource?: string;
  options: Option[];
  multipleChoice?: boolean;
}

export interface VotingSlide extends Slide {
  voteCount: number;
  voteLabel: string;
}

export interface Option {
  id: string;
  name?: string;
  content: string;
  contentSource?: string;
  contentCopyright?: string;
  description?: string;
  type: string;
  answer?: boolean;
  answer_text?: string;
  answer_image?: string;
  answer_imageSource?: string;
  answer_imageCopyright?: string;
  branch?: string;
}

export type Participant = {
  email: string;
  interactionType: string;
  interactionId: string;
  firstName: string;
  lastName: string;
  advertisementOptIn?: boolean;
  hansIdOptIn?: boolean;
  hansId?: string;
  campaignId?: string;
  gender?: string;
};

export enum InteractionLayout {
  Text = "text",
  Images = "images",
  Light = "light",
  Fullscreen = "fullscreen",
  Meter = "meter",
  Compass = "compass",
  Thumbs = "thumbs",
  Nps = "nps",
}

export enum InteractionType {
  Poll = "poll",
  Quiz = "quiz",
  Voting = "voting",
  Opinion = "opinion",
}

/**
 * Intro Configuration
 * @description Implements models for intro type.
 */
export interface InteractionIntro {
  title: string;
  text: string;
  label: string;
  background?: string;
  backgroundSource?: string;
  backgroundCopyright?: string;
}

/**
 * Registration Status:
 * @description represents the current status of a registration request to backend.
 */
export enum RegistrationStatus {
  None = "none",
  Pending = "pending",
  Success = "success",
  Error = "error",
}

/**
 * Outro Configuration
 * @description Implements models for outro types.
 */
export enum OutroType {
  Outlink = "outlink",
  Advertising = "advertising",
  RegistrationForm = "registrationForm",
  Request = "request",
  RequestOrAdvertising = "request_advertising",
}

export type InteractionOutro =
  | OutlinkInteractionOutro
  | AdvertisingInteractionOutro
  | RegistrationFormInteractionOutro
  | QuestionInteractionOutro
  | QuestionOrAdvertisingOutro;

export interface OutlinkInteractionOutro {
  outroType: OutroType.Outlink;
  showOnlyForCorrectAnswers: boolean;
  autoForwardTimerEnabled?: boolean;
  autoForwardTimerSeconds?: number;
  outlinkUrl: string;
  iosOutlinkUrl?: string;
  androidOutlinkUrl?: string;
  background?: string;
  backgroundSource?: string;
  backgroundCopyright?: string;
  mobileBackground?: string;
  mobileBackgroundSource?: string;
  mobileBackgroundCopyright?: string;
  campaignId?: string;
}

export interface AdvertisingInteractionOutro {
  outroType: OutroType.Advertising;
  advertisingAdSlot?: string;
  advertisingWidth?: string;
  advertisingHeight?: string;
  showOnlyForCorrectAnswers?: boolean;
  campaignId?: string;
}

export interface RegistrationFormInteractionOutro {
  outroType: OutroType.RegistrationForm;
  termsAndConditionsLink: string;
  privacyPolicyLink: string;
  showOnlyForCorrectAnswers?: boolean;
  advertisementOptIn?: boolean;
  hansIdOptIn?: boolean;
  onlyWithRegistration?: boolean;
  lightRegistration?: boolean; // Request only name and email from the user
  outroTitle?: string;
  customFields?: CustomField[];
  campaignId?: string;
}

export enum CustomFieldType {
  Input = "input",
  Select = "select",
}

export interface CustomField {
  type: CustomFieldType;
  label: string;
  options?: string[];
}

export interface QuestionInteractionOutro {
  outroType: OutroType.Request;
  genderRequest: boolean;
  ageRequest: boolean;
  incomeRequest: boolean;
  showOnlyForCorrectAnswers?: boolean;
  campaignId?: string;
}

export interface QuestionOrAdvertisingOutro {
  outroType: OutroType.RequestOrAdvertising;
  genderRequest: boolean;
  ageRequest: boolean;
  incomeRequest: boolean;
  showOnlyForCorrectAnswers: boolean;
  advertisingAdSlot?: string;
  advertisingWidth?: string;
  advertisingHeight?: string;
  campaignId?: string;
}

/**
 * Interaction
 * @description Implements interface for generic interaction
 */
export type BaseInteraction = {
  id: string;
  type: InteractionType;
  layout: InteractionLayout;
  title: string;
  sharingTitle?: string;
  organizationId: string;
  category: string;
  endDate?: number;
  startDate?: number;
  hideParticipantsCount?: boolean;
  hideResults?: boolean;
  secret?: boolean;
};

/**
 * Poll Interaction
 * @description Implements interface for poll object and results response
 */
export type Poll = BaseInteraction & {
  type: InteractionType.Poll;
  hideTitle?: boolean;
  background?: string;
  backgroundSource?: string;
  backgroundCopyright?: string;
  useBackgroundColor?: boolean;
  backgroundColor?: string;
  evaluationText?: string;
  evaluationTextarea?: string;
  intro?: InteractionIntro;
  outro?: InteractionOutro;
  slides: Slide[];
  secretPoll?: boolean;
};

export type Vote = PollVote | QuizVote | VotingVote;

export interface PollVote {
  interactionId: string;
  slideId: string;
  optionId: string;
}

export interface PollResult extends PollVote {
  value: number;
}

export interface PollResultsContainer {
  participantsCount: number;
  results: PollResult[];
}

/**
 * Poll Interaction
 * @description Implements interface for voting object and results response
 */
export type Voting = BaseInteraction & {
  type: InteractionType.Voting;
  hideTitle?: boolean;
  background?: string;
  backgroundSource?: string;
  backgroundCopyright?: string;
  backgroundLabel?: string;
  useBackgroundColor?: boolean;
  backgroundColor?: string;
  evaluationText?: string;
  evaluationTextarea?: string;
  intro?: InteractionIntro;
  outro?: InteractionOutro;
  slides: VotingSlide[];
};

export interface VotingVote {
  slideId: string;
  optionId: string;
}

export interface VotingResult extends VotingVote {
  value: number;
}

export interface VotingResultsContainer {
  participantsCount: number;
  results: VotingResult[];
}

/**
 * Opinion Interaction
 * @description Implements interface for opinion object and results response
 */
export type Opinion = BaseInteraction & {
  type: InteractionType.Opinion;
  subtitle?: string;
  answerLeft: string;
  answerRight: string;
  opinions?: OpinionVote[];
  conditionalFeedback?: ConditionalFeedback[];
  thankYouText?: string;
  outro?: InteractionOutro;
};

export type ConditionalFeedback = {
  condition: number;
  text: string;
  label: string;
};

export type OpinionVote = {
  iconUrl: string;
  name: string;
  opinion: number;
  description: string;
  opinionOutLink?: string;
};

export interface OpinionResult {
  opinionId: string;
  value: number[];
  participantsCount?: number;
}

export type InteractionScoreResult = {
  id: string;
  range: number;
  text?: string;
  image?: string;
};

export type InteractionScore = {
  maxScore: number;
  result?: InteractionScoreResult[];
};

/**
 * Quiz Interaction
 * @description Implements interface for quiz object and results response
 */
export type Quiz = BaseInteraction & {
  type: InteractionType.Quiz;
  hideTitle?: boolean;
  background?: string;
  backgroundSource?: string;
  backgroundCopyright?: string;
  backgroundLabel?: string;
  evaluationText?: string;
  useBackgroundColor?: boolean;
  backgroundColor?: string;
  intro?: InteractionIntro;
  outro?: InteractionOutro;
  slides: Slide[];
  showCorrectAnswer?: boolean;
  score?: InteractionScore;
};

export interface QuizVote {
  quizId: string;
  slideId: string;
  optionId: string;
  value: number;
  answer: boolean;
  correctAnswers?: Option[];
}

export interface QuizVoteResponse {
  correctAnswerCount: number;
  correctAnswers?: Option[];
}

export interface QuizResult extends QuizVote {
  value: number;
}

export interface QuizResultsContainer {
  participantsCount: number;
  results: QuizResult[];
}

export type ImageData = {
  url: string;
  source: string;
  copyright: string;
};

export type Interaction = Poll | Opinion | Quiz | Voting;
export type InteractionResult =
  | PollResultsContainer
  | OpinionResult
  | QuizResultsContainer
  | VotingResultsContainer;

export type HansIdResponse = {
  hansId: string;
  consent: boolean;
};

export type AdvertisementConsent = {
  interactionId: string;
  hansId: string;
  consentAt: string;
};
